import React from 'react';
import { Instagram, Mail, Phone, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const ComingSoon = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1e3a8a] via-[#2563eb] to-[#1e3a8a] relative overflow-hidden">
      {/* Navigation Header */}
      <header className="relative z-20 bg-white/10 backdrop-blur-md border-b border-white/20">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_b5d7f5d1-8fe3-4df9-bbe6-0522c761a4fd/artifacts/prqfh8ph_WhatsApp%20Image%202026-02-07%20at%2022.24.51.jpeg" 
                alt="BRISE Logo" 
                className="h-14 w-auto"
              />
            </Link>
            
            <nav className="flex items-center gap-6">
              <Link 
                to="/about" 
                className="text-white hover:text-[#fbbf24] font-semibold transition-colors duration-200 text-lg"
              >
                About Us
              </Link>
              <Link 
                to="/founders" 
                className="text-white hover:text-[#fbbf24] font-semibold transition-colors duration-200 text-lg"
              >
                Founder's Desk
              </Link>
              <Link to="/contact">
                <Button className="bg-[#fbbf24] hover:bg-[#f59e0b] text-[#1e3a8a] font-semibold transition-all duration-300">
                  Contact Us
                </Button>
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#fbbf24]/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#fbbf24]/15 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-white/10 rounded-full blur-2xl animate-pulse" style={{animationDelay: '2s'}}></div>
      </div>

      {/* Floating Solar Icons */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-32 right-1/4 text-[#fbbf24]/30 animate-float">
          <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 24 24">
            <rect x="4" y="4" width="6" height="6" />
            <rect x="14" y="4" width="6" height="6" />
            <rect x="4" y="14" width="6" height="6" />
            <rect x="14" y="14" width="6" height="6" />
          </svg>
        </div>
        <div className="absolute bottom-40 left-1/4 text-[#fbbf24]/20 animate-float" style={{animationDelay: '0.5s'}}>
          <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="8" />
            <path d="M12 2 L12 6 M12 18 L12 22 M2 12 L6 12 M18 12 L22 12 M5 5 L8 8 M16 16 L19 19 M5 19 L8 16 M16 8 L19 5" stroke="currentColor" strokeWidth="2"/>
          </svg>
        </div>
      </div>

      <div className="relative z-10 min-h-[calc(100vh-80px)] flex items-center justify-center px-6">
        <div className="max-w-4xl w-full text-center">
          {/* Logo and Title */}
          <div className="mb-8 animate-fade-in">
            <h1 className="text-6xl md:text-7xl font-bold text-white mb-4">BRISE</h1>
            <p className="text-2xl text-[#fbbf24] font-semibold">
              Bharat Renewable Infrastructure & Solar Energy
            </p>
          </div>

          {/* Coming Soon */}
          <div className="my-12 animate-fade-in" style={{animationDelay: '0.2s'}}>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Coming Soon
            </h2>
            <p className="text-xl text-gray-100 mb-8">
              We're building something amazing for India's renewable energy future
            </p>
          </div>

          {/* Instagram Message Card */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 md:p-12 border-2 border-[#fbbf24]/30 mb-12 animate-fade-in" style={{animationDelay: '0.4s'}}>
            <div className="flex items-center justify-center gap-3 mb-6">
              <Instagram className="h-8 w-8 text-[#fbbf24]" />
              <h3 className="text-3xl font-bold text-white">Join Our Journey!</h3>
            </div>
            
            <div className="bg-white/5 rounded-2xl p-6 mb-6 border border-white/20">
              <p className="text-xl text-white leading-relaxed mb-4">
                While our website is under construction, we're excited to share our 
                <span className="text-[#fbbf24] font-bold"> 90-Day Knowledge Series </span>
                on Instagram!
              </p>
              <p className="text-lg text-gray-200">
                📚 Daily insights on solar energy<br />
                💡 Industry trends and innovations<br />
                🌱 Sustainability tips and best practices
              </p>
            </div>

            <a 
              href="https://www.instagram.com/briseenergy?igsh=MWF5cHd1dzhqYWt1NA==" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] hover:from-[#f59e0b] hover:to-[#fbbf24] text-[#1e3a8a] font-bold px-10 py-7 text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-2xl"
              >
                <Instagram className="mr-3 h-6 w-6" />
                Follow @briseenergy on Instagram
              </Button>
            </a>
          </div>

          {/* Contact Info */}
          <div className="grid md:grid-cols-3 gap-6 text-white animate-fade-in" style={{animationDelay: '0.6s'}}>
            <div className="flex items-center justify-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-all">
              <Mail className="h-5 w-5 text-[#fbbf24]" />
              <a href="mailto:pulkit.garg@briseenergy.com" className="text-sm hover:text-[#fbbf24] transition-colors">
                pulkit.garg@briseenergy.com
              </a>
            </div>
            <div className="flex items-center justify-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-all">
              <Phone className="h-5 w-5 text-[#fbbf24]" />
              <a href="tel:+916396455851" className="text-sm hover:text-[#fbbf24] transition-colors">
                +91 6396455851
              </a>
            </div>
            <div className="flex items-center justify-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-all">
              <MapPin className="h-5 w-5 text-[#fbbf24]" />
              <span className="text-sm">Dehradun, India</span>
            </div>
          </div>

          {/* Footer Note */}
          <div className="mt-12 text-gray-300 text-sm animate-fade-in" style={{animationDelay: '0.8s'}}>
            <p>© 2026 BRISE Private Limited. All Rights Reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComingSoon;
